import React from "react";

export function roundTo(n) {
  return Math.round(n);
}

export function roundedOrthPath(points, r = 8) {
  if (!points || points.length < 2) return "";
  const p0 = points[0];
  let d = `M ${p0.x} ${p0.y} `;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    if (i === points.length - 1) {
      d += `L ${cur.x} ${cur.y} `;
      break;
    }
    const next = points[i + 1];
    const dx = cur.x - prev.x;
    const dy = cur.y - prev.y;
    const dx2 = next.x - cur.x;
    const dy2 = next.y - cur.y;
    const lenIn = Math.hypot(dx, dy) || 1;
    const lenOut = Math.hypot(dx2, dy2) || 1;
    const rActual = Math.min(r, lenIn / 2, lenOut / 2);
    const before = {
      x: cur.x - (dx / lenIn) * rActual,
      y: cur.y - (dy / lenIn) * rActual,
    };
    const after = {
      x: cur.x + (dx2 / lenOut) * rActual,
      y: cur.y + (dy2 / lenOut) * rActual,
    };
    d += `L ${before.x} ${before.y} Q ${cur.x} ${cur.y} ${after.x} ${after.y} `;
  }
  return d.trim();
}

export default function BaseEdge({
  d,
  stroke = "#333",
  strokeWidth = 2,
  onSelect,
  onStartDrag,
  label,
  labelPos,
  isInChain = false,
  isCriticalEdge = false,
  isHovered,
  isSelected,
  onHoverEnter,
  onHoverLeave,
  opacity = 1.0,
  strokeDasharray = "0",
}) {
  // Determine stroke width based on state
  const finalStrokeWidth = isSelected ? 4 : isHovered ? 3 : strokeWidth;

  // Determine marker based on critical status
  const markerEnd = isCriticalEdge
    ? "url(#arrow-flow-critical)"
    : "url(#arrow-flow-normal)";

  // Determine glow color based on state
  const getGlowColor = () => {
    if (isCriticalEdge) return "rgba(204, 0, 0, 0.85)"; // Red for critical
    if (isInChain) return "rgba(26, 115, 232, 0.85)"; // Blue for chain
    return "rgba(0, 150, 255, 0.85)"; // Blue for normal hover
  };

  return (
    <g
      style={{
        cursor: "pointer",
        opacity: opacity,
        transition: "opacity 0.3s ease, filter 0.2s ease",
        filter:
          isHovered && opacity > 0.5
            ? `drop-shadow(0 0 6px ${getGlowColor()})`
            : "none",
      }}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      onClick={(e) => {
        e.stopPropagation();
        onSelect && onSelect(e);
      }}
    >
      {/* Enhanced shadow for chain edges */}
      {isInChain && (
        <path
          d={d}
          stroke="rgba(0, 0, 0, 0.25)"
          strokeWidth={finalStrokeWidth + 6}
          fill="none"
          strokeOpacity={opacity * 0.8}
          style={{ pointerEvents: "none" }}
        />
      )}

      {/* Main edge path */}
      <path
        d={d}
        stroke={stroke}
        strokeWidth={finalStrokeWidth}
        strokeOpacity={opacity}
        fill="none"
        markerEnd={markerEnd}
        strokeDasharray={strokeDasharray}
        style={{
          transition: "stroke-width 0.2s ease, stroke-opacity 0.3s ease",
        }}
      />

      {/* HIT AREA - larger for chain and critical edges */}
      <path
        d={d}
        stroke="transparent"
        strokeWidth={
          isInChain
            ? 24
            : opacity < 0.5
            ? 6 // 👈 smaller hit area
            : 12
        }
        fill="none"
        style={{ pointerEvents: "stroke" }}
        onMouseDown={(e) => {
          e.stopPropagation();
          onStartDrag && onStartDrag(e);
        }}
      />

      {/* Label (if provided) */}
      {label && labelPos && (
        <g
          transform={`translate(${labelPos.x}, ${labelPos.y})`}
          style={{ pointerEvents: "none" }}
        >
          <rect
            x={-40}
            y={-12}
            width={80}
            height={20}
            rx={6}
            ry={6}
            fill="white"
            stroke="#ccc"
            opacity={opacity}
          />
          <text
            x={0}
            y={4}
            textAnchor="middle"
            fontSize="11"
            fill="red"
            style={{ fontWeight: 500, opacity: opacity }}
          >
            {label}
          </text>
        </g>
      )}
    </g>
  );
}
